import React from "react";
import { Box } from "@mui/material";

type MagnifierImageProps = {
  src: string;
  alt?: string;
  /** zoom multiplier; 2 = 200% */
  zoom?: number;
};

const MagnifierImage: React.FC<MagnifierImageProps> = ({
  src,
  alt,
  zoom = 2,
}) => {
  const [zoomed, setZoomed] = React.useState(false);
  const [pos, setPos] = React.useState("50% 50%");
  const rafRef = React.useRef<number | null>(null);

  const clampPct = (n: number) => Math.max(0, Math.min(100, n));

  const updatePos = (x: number, y: number, rect: DOMRect) => {
    const relX = ((x - rect.left) / rect.width) * 100;
    const relY = ((y - rect.top) / rect.height) * 100;
    const cx = clampPct(relX);
    const cy = clampPct(relY);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setPos(`${cx}% ${cy}%`);
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!zoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    updatePos(e.clientX, e.clientY, rect);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!zoomed) return;
    const touch = e.touches[0];
    if (!touch) return;
    const rect = e.currentTarget.getBoundingClientRect();
    updatePos(touch.clientX, touch.clientY, rect);
  };

  const toggleZoom = () => setZoomed((z) => !z);
  const leave = () => setZoomed(false);

  return (
    <Box
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onClick={toggleZoom}
      onTouchStart={toggleZoom}
      onMouseLeave={leave}
      onTouchEnd={leave}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        aspectRatio: "1 / 1",
        backgroundColor: "#fff",
        cursor: zoomed ? "zoom-out" : "zoom-in",
        backgroundImage: `url(${src})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: zoomed ? `${zoom * 100}% auto` : "contain",
        backgroundPosition: pos,
        transition: "background-size 200ms ease",
      }}
      role="img"
      aria-label={alt}
    />
  );
};

export default MagnifierImage;
